import { Tooltip, TooltipTrigger, WithNAFallback } from '@toggle/design-system';
import { getPriceStatus, Status } from '@toggle/helpers';
import { MappedEntity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Identifier } from '~/hooks/use-live-price';
import { useOneDayReturnLivePrice } from '~/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { formatPercentageNumber } from '~/widgets/earnings/utils/utils';

import { PriceCellTooltip } from '../price-cell/price-cell-tooltip/PriceCellTooltip';
import { getPriceStamp } from '../price-display-view/helpers/price-display-helpers';
import * as S from './OneDayReturnCell.styles';

interface OneDayReturnCellProps {
  asset?: MappedEntity;
  isDisabled?: boolean;
  showLiveIndicator?: boolean;
  showPercentageSuffix?: boolean;
}

export const OneDayReturnCell = ({
  asset,
  isDisabled = false,
  showLiveIndicator,
  showPercentageSuffix = false,
}: OneDayReturnCellProps) => {
  const { t } = useTranslation(['widget', 'common', 'watchlistManager']);
  const {
    oneDayLiveReturn,
    isLive,
    isLivePriceSupported,
    date,
    isSubscribing,
  } = useOneDayReturnLivePrice(asset, Identifier.Watchlist);

  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled,
  });

  const status = getPriceStatus(oneDayLiveReturn);

  if (isSubscribing) {
    return <S.StyledSkeletonLoader />;
  }

  return (
    <WithNAFallback
      check={oneDayLiveReturn !== undefined}
      tooltip={t('widget:watchlist.noLiveDataAvailable')}
      value={
        oneDayLiveReturn !== undefined && (
          <Tooltip
            isTouchDevice={false}
            trigger={TooltipTrigger.Hover}
            label={
              date && <PriceCellTooltip type={stamp.stampType} date={date} />
            }
            inPortal
            disabled={!date}
          >
            <S.FlexRowWrapper>
              <S.PriceWrapper $status={isDisabled ? Status.Disabled : status}>
                {formatPercentageNumber({
                  value: oneDayLiveReturn,
                  includePercentSign: showPercentageSuffix,
                  addPlus: true,
                })}
              </S.PriceWrapper>
              {showLiveIndicator && (
                <S.Indicator $status={isLive ? 'livePrice' : 'default'} />
              )}
            </S.FlexRowWrapper>
          </Tooltip>
        )
      }
    />
  );
};
